/**
 * Die Gesamtuebersicht (V-01, seit V-15 sieben Bloecke; seit D-07 als anpassbare Kacheln).
 *
 * 1. Heute - Aufgaben und der Fokus der Woche
 * 2. Ueberfaellig
 * 3. Gewohnheiten heute
 * 4. Bereiche - Status aller Bereiche mit dem Lebensrad
 * 5. Kalender - heute und die naechsten zwei Tage
 * 6. Blick in die Zukunft - die naechsten sieben Tage
 * 7. Zeitverteilung - heute bzw. diese Woche (V-15, F-28)
 *
 * Welche Kacheln wo stehen, legt `domain/oberflaeche/kacheln.ts` fest (Standard) bzw. Ole selbst (Anpassen).
 * Alles andere liegt auf den Boards. Die Seite ist zum Anschauen da (V-02):
 * nur Links und Checkboxen, kein Eingabefeld. Sie liest aus denselben Hooks
 * wie die Fachseiten und respektiert damit den Bereichsfilter.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, Plus, Search } from 'lucide-react';

import { PageHeader } from '@/app/layout/PageHeader';
import { Page } from '@/ui/composites/Page';
import { holidayMap } from '@/lib/holidays/de';
import { useWeather } from '@/lib/weather/useWeather';
import { useWeatherSettings } from '@/modules/settings/useLocation';
import { describeWeather } from '@/lib/weather/model';
import { WetterSymbol } from '@/ui/composites/WetterSymbol';
import { formatDe, today } from '@/lib/datetime';
import { de } from '@/i18n/de';
import { useSuche } from '@/modules/suche/sucheStore';
import { useBundesland } from '@/modules/school/useSchool';
import { KachelRaster } from '@/modules/kacheln/KachelRaster';

export function DashboardPage() {
  const heute = today();

  const [bearbeiten, setBearbeiten] = useState(false);
  const sucheOeffnen = useSuche((s) => s.oeffnen);

  const { location, enabled: weatherEnabled } = useWeatherSettings();
  const weather = useWeather(heute, heute, location, weatherEnabled).get(heute);
  const land = useBundesland();
  const feiertag = holidayMap(heute, heute, land).get(heute);

  return (
    <Page>
      <PageHeader
        title={formatDe(heute, 'EEEE, d. MMMM')}
        description={de.app.tagline}
        action={(
          // Nur Navigation, kein Eingabefeld (V-02): geschrieben wird in Resources (V-12), gesucht im Suchfenster (F-18).
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => sucheOeffnen()}
              className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-sm text-ink-muted transition-colors hover:border-line-strong hover:text-ink">
              <Search className="size-4" /> Suchen
            </button>
            <button type="button" onClick={() => setBearbeiten((b) => !b)} aria-pressed={bearbeiten}
              className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-sm text-ink-muted transition-colors hover:border-line-strong hover:text-ink aria-pressed:border-accent aria-pressed:text-ink">
              <LayoutGrid className="size-4" /> {bearbeiten ? 'Fertig' : 'Anpassen'}
            </button>
            <Link to="/ressourcen?neu=1" className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-on-accent transition-opacity hover:opacity-90">
              <Plus className="size-4" /> {de.resources.neu}
            </Link>
          </div>
        )}
      />

      {/* Datensicherheit zuerst (F-37) - nur sichtbar, wenn der Speicher nicht dauerhaft ist. */}

      {(weather || feiertag) && (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-muted">
          {weather && (
            <span className="inline-flex items-center gap-1.5">
              <WetterSymbol code={weather.code} className="size-4" />
              <span>
                {describeWeather(weather.code).label}, {Math.round(weather.minC)} bis{' '}
                {Math.round(weather.maxC)} °C
                {weather.rainChance !== null && weather.rainChance >= 30
                  && ` · ${weather.rainChance} % Regen`}
              </span>
            </span>
          )}
          {feiertag && <span className="text-accent-text">{feiertag}</span>}
        </p>
      )}

      <KachelRaster layoutId="uebersicht" bearbeiten={bearbeiten} />
    </Page>
  );
}
